import {
  getPushConfig,
  getPushPreferences,
  listPushDevices,
  registerPushSubscription,
  removePushSubscription,
} from './push.api';

import type { WebPushPreferences } from '@utazzo/shared';

import { env } from '@/env';
import { describeThisDevice, isAppleMobile, isStandaloneApp } from '@/lib/device';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Browser side of admin web push. Plain module (no React) so main.tsx, the
 * logout flow and the Settings card can all use it.
 *
 * Lifecycle:
 *   - `registerServiceWorker()` - main.tsx, every load. Push-only worker.
 *   - `enablePushOnThisDevice()` - Settings button (a user gesture; iPhone
 *     refuses the permission prompt without one).
 *   - `syncPushSubscription()` - every app open while signed in: re-binds this
 *     device to whoever is signed in, and repairs a rotated key/subscription.
 *   - `releasePushForLogout()` - manual logout: this device stops receiving.
 *
 * Every function is safe to call on a browser without push: it resolves
 * quietly instead of throwing.
 */

const SW_URL = '/sw.js';
/** Id of the user who switched push on here; cleared on "turn off" / manual logout. */
const OPT_IN_KEY = 'utazzo.push.optIn';
/** Endpoint the server last confirmed for this device; cleared with the opt-in. */
const ENDPOINT_KEY = 'utazzo.push.endpoint';

export type PushSupport =
  /** Service worker + Push API available. */
  | 'supported'
  /** iPhone / iPad in a Safari tab: push only exists in the home-screen app. */
  | 'needs-install'
  /** No push in this browser (old browser, in-app webview, mocks mode). */
  | 'unsupported';

export const pushSupport = (): PushSupport => {
  if (typeof window === 'undefined' || env.VITE_ENABLE_MOCKS) return 'unsupported';
  const hasApis =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (hasApis) return 'supported';
  return isAppleMobile() && !isStandaloneApp() ? 'needs-install' : 'unsupported';
};

// ---------------------------------------------------------------------------
// Service worker
// ---------------------------------------------------------------------------

let registration: Promise<ServiceWorkerRegistration | null> | null = null;

/**
 * Register the push worker. Idempotent. Skipped in mocks mode, where MSW's
 * own worker owns the `/` scope (two workers can't share a scope).
 */
export const registerServiceWorker = (): Promise<ServiceWorkerRegistration | null> => {
  if (registration) return registration;
  if (typeof window === 'undefined' || env.VITE_ENABLE_MOCKS || !('serviceWorker' in navigator)) {
    registration = Promise.resolve(null);
    return registration;
  }
  registration = navigator.serviceWorker
    // `updateViaCache: 'none'` - always revalidate sw.js itself, so a fix to
    // the worker reaches everyone on their next load.
    .register(SW_URL, { scope: '/', updateViaCache: 'none' })
    .catch(() => null);
  return registration;
};

// ---------------------------------------------------------------------------
// State shared with the rest of the app
// ---------------------------------------------------------------------------

let activeHere = false;
/** Set when the last sync hit a transient error, so it is worth retrying. */
let lastSyncFailed = false;
/**
 * Bumped by every deliberate change of this device's push state - turn on,
 * turn off, logout, a different user taking over. A sync that started before
 * the bump is stale: it must neither switch push back on nor undo the newer
 * choice (turning off while the app-open sync was still on the network used
 * to re-subscribe the device behind the user's back).
 */
let pushEpoch = 0;
/** The signed-in user's push switches, as last read from the server. */
type CoveredCategory = 'notifications' | 'whatsappMessages' | 'whatsappCalls';
let coveredCategories: Pick<WebPushPreferences, CoveredCategory> | null = null;
const listeners = new Set<() => void>();

const notify = (): void => {
  for (const l of listeners) l();
};

const setActive = (next: boolean): void => {
  if (activeHere === next) return;
  activeHere = next;
  notify();
};

/** True when this device is subscribed and registered for the signed-in user. */
export const isPushActiveHere = (): boolean => activeHere;

/**
 * True when a web push for this category will reach THIS device. In-page
 * notifications step aside only then - the push already covers them, and
 * showing both would double every alert. Per category: a user who switched,
 * say, WhatsApp messages off for their phones must keep the in-page alert.
 * Unknown preferences count as "not covered" (a duplicate beats a miss).
 */
export const isPushCovering = (category: CoveredCategory): boolean =>
  activeHere && coveredCategories?.[category] === true;

/** Keep the category cache in step with the Settings card. */
export const setCachedPushPreferences = (prefs: WebPushPreferences | null): void => {
  coveredCategories = prefs
    ? {
        notifications: prefs.notifications,
        whatsappMessages: prefs.whatsappMessages,
        whatsappCalls: prefs.whatsappCalls,
      }
    : null;
  notify();
};

export const onPushActiveChange = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const currentUserId = (): string | null => useAuthStore.getState().user?.id ?? null;

/**
 * Who switched push on here (a user id), or null. Per user, not per device: on
 * a shared laptop the next person to sign in must not silently inherit it.
 */
const optInOwner = (): string | null => {
  try {
    return localStorage.getItem(OPT_IN_KEY);
  } catch {
    return null;
  }
};

/**
 * True when the signed-in user switched push on for this device. The sync on
 * every app open keeps such a device live, so the "turn on notifications"
 * nudge stays away from it even before that sync has finished.
 */
export const isOptedInHere = (): boolean => {
  const me = currentUserId();
  return me !== null && optInOwner() === me;
};

const setOptIn = (userId: string | null): void => {
  try {
    if (userId) localStorage.setItem(OPT_IN_KEY, userId);
    else {
      localStorage.removeItem(OPT_IN_KEY);
      localStorage.removeItem(ENDPOINT_KEY);
    }
  } catch {
    // Private mode - worst case the user taps "Turn on" again next time.
  }
};

/**
 * The subscription endpoint the server last CONFIRMED for this device. Only
 * when that exact endpoint vanishes from the server's list was the device
 * removed on purpose (from "Your devices" elsewhere); a different endpoint
 * is a browser rotation or another tab's fresh subscription - re-register it.
 */
const confirmedEndpoint = (): string | null => {
  try {
    return localStorage.getItem(ENDPOINT_KEY);
  } catch {
    return null;
  }
};

const setConfirmedEndpoint = (endpoint: string): void => {
  try {
    localStorage.setItem(ENDPOINT_KEY, endpoint);
  } catch {
    // Private mode - removal elsewhere then just won't stick on this device.
  }
};

// ---------------------------------------------------------------------------
// Subscription helpers
// ---------------------------------------------------------------------------

const base64UrlToBytes = (value: string): Uint8Array<ArrayBuffer> => {
  const padded = value
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(value.length / 4) * 4, '=');
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
};

const sameKey = (sub: PushSubscription, publicKey: string): boolean => {
  const current = sub.options?.applicationServerKey;
  if (!current) return true; // Browser doesn't expose it - can't tell, assume fine.
  const a = new Uint8Array(current);
  const b = base64UrlToBytes(publicKey);
  return a.length === b.length && a.every((v, i) => v === b[i]);
};

const READY_TIMEOUT_MS = 5_000;

/**
 * The active worker registration, or null. `serviceWorker.ready` never
 * settles when no worker ever activates, so it is raced against a timeout -
 * nothing here (logout included) may hang on it.
 */
export const readyRegistration = async (): Promise<ServiceWorkerRegistration | null> => {
  const reg = await registerServiceWorker();
  if (!reg) return null;
  try {
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), READY_TIMEOUT_MS)),
    ]);
  } catch {
    return null;
  }
};

export const currentSubscription = async (): Promise<PushSubscription | null> => {
  if (pushSupport() !== 'supported') return null;
  const reg = await readyRegistration();
  if (!reg) return null;
  try {
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
};

const sendToServer = async (sub: PushSubscription): Promise<void> => {
  const json = sub.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!json.endpoint || !p256dh || !auth) throw new Error('Incomplete push subscription.');
  await registerPushSubscription({
    endpoint: json.endpoint,
    keys: { p256dh, auth },
    deviceLabel: describeThisDevice(),
  });
};

// ---------------------------------------------------------------------------
// Public actions
// ---------------------------------------------------------------------------

export type EnableResult =
  | { ok: true }
  | {
      ok: false;
      reason: 'unsupported' | 'needs-install' | 'denied' | 'unavailable' | 'failed';
      message?: string;
    };

/**
 * Switch push on for this device. MUST be called straight from a click, with
 * the VAPID key and worker registration loaded beforehand: the permission
 * prompt is requested before anything is awaited, because Safari drops the
 * user gesture across an await and then refuses to show it.
 */
export const enablePushOnThisDevice = async (
  publicKey: string,
  reg: ServiceWorkerRegistration,
): Promise<EnableResult> => {
  const support = pushSupport();
  if (support !== 'supported') return { ok: false, reason: support };
  const me = currentUserId();
  if (!me) return { ok: false, reason: 'failed', message: 'Sign in again, then retry.' };

  // First await of this function - still inside the click's gesture.
  let permission = Notification.permission;
  if (permission === 'default') {
    try {
      permission = await Notification.requestPermission();
    } catch {
      permission = Notification.permission;
    }
  }
  if (permission === 'denied') return { ok: false, reason: 'denied' };
  if (permission !== 'granted') {
    return { ok: false, reason: 'failed', message: 'Notifications were not allowed.' };
  }

  let sub: PushSubscription;
  try {
    // Permission is granted now, so `subscribe()` needs no gesture of its own.
    const existing = await reg.pushManager.getSubscription().catch(() => null);
    if (existing && !sameKey(existing, publicKey)) await existing.unsubscribe().catch(() => false);
    sub =
      existing && sameKey(existing, publicKey)
        ? existing
        : await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: base64UrlToBytes(publicKey),
          });
  } catch (err) {
    if (Notification.permission === 'denied') return { ok: false, reason: 'denied' };
    return {
      ok: false,
      reason: 'failed',
      message: err instanceof Error ? err.message : undefined,
    };
  }

  try {
    await sendToServer(sub);
  } catch (err) {
    return {
      ok: false,
      reason: 'failed',
      message: err instanceof Error ? err.message : undefined,
    };
  }
  pushEpoch++;
  setOptIn(me);
  setConfirmedEndpoint(sub.endpoint);
  setCachedPushPreferences(await getPushPreferences().catch(() => null));
  setActive(true);
  lastSyncFailed = false;
  return { ok: true };
};

/** "Turn off on this device": server row gone, browser subscription dropped. */
export const disablePushOnThisDevice = async (): Promise<void> => {
  pushEpoch++;
  setOptIn(null);
  setActive(false);
  setCachedPushPreferences(null);
  const sub = await currentSubscription();
  if (!sub) return;
  try {
    await removePushSubscription(sub.endpoint);
  } finally {
    await sub.unsubscribe().catch(() => false);
  }
};

/**
 * Keep this device's registration true to reality. Called on every app open
 * while signed in (and when the worker reports a rotated subscription):
 *   - not opted in, or permission gone   -> inactive, nothing sent;
 *   - opted in by ANOTHER user            -> cut the device off (their row
 *     dies at the next 404/410); this user opts in themselves;
 *   - removed from another device         -> off here too, until someone taps
 *     "Turn on" on THIS device (the endpoint the server last confirmed for
 *     this device is gone from its list - deleted from "Your devices"
 *     elsewhere; an unconfirmed endpoint is a rotation, and re-registers);
 *   - key rotated on the server           -> re-subscribe with the new key;
 *   - subscription lost (browser cleared) -> subscribe again (no prompt:
 *     permission is already granted);
 *   - otherwise                           -> re-register + refresh settings.
 * A transient failure (offline, timeout) leaves the state as it was - a
 * device that was delivering keeps delivering, and flipping it to inactive
 * would double every alert - and is retried by `retryPushSyncIfFailed`.
 * A deliberate change made while it runs (see `pushEpoch`) always wins.
 * Never throws.
 */
export const syncPushSubscription = async (): Promise<void> => {
  const epoch = pushEpoch;
  try {
    const me = currentUserId();
    const owner = optInOwner();
    if (pushSupport() !== 'supported' || !me || !owner || Notification.permission !== 'granted') {
      setActive(false);
      lastSyncFailed = false;
      return;
    }
    if (owner !== me) {
      pushEpoch++;
      setOptIn(null);
      setActive(false);
      setCachedPushPreferences(null);
      lastSyncFailed = false;
      const previous = await currentSubscription();
      await previous?.unsubscribe().catch(() => false);
      return;
    }
    /** Nothing deliberate happened since this sync began. */
    const current = (): boolean => pushEpoch === epoch && optInOwner() === me;

    const config = await getPushConfig();
    if (!current()) return;
    if (!config.enabled || !config.publicKey) {
      setActive(false);
      return;
    }
    const reg = await readyRegistration();
    if (!reg || !current()) {
      if (!reg) setActive(false);
      return;
    }
    let sub = await reg.pushManager.getSubscription();
    if (!current()) return;
    if (sub && !sameKey(sub, config.publicKey)) {
      await sub.unsubscribe().catch(() => false);
      sub = null;
    }
    if (sub && sub.endpoint === confirmedEndpoint()) {
      const onServer = await listPushDevices();
      if (!current()) return;
      const endpoint = sub.endpoint;
      if (!onServer.some((d) => d.endpoint === endpoint)) {
        // Removed from "Your devices" on another device - honour it here.
        pushEpoch++;
        setOptIn(null);
        setActive(false);
        setCachedPushPreferences(null);
        lastSyncFailed = false;
        await sub.unsubscribe().catch(() => false);
        return;
      }
    }
    const created = !sub;
    sub ??= await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToBytes(config.publicKey),
    });
    if (!current()) {
      if (created && optInOwner() !== me) await sub.unsubscribe().catch(() => false);
      return;
    }
    await sendToServer(sub);
    if (!current()) {
      // Turned off / logged out while we were registering. Take back what we
      // just re-registered - unless a newer "Turn on" now owns this device.
      if (optInOwner() !== me) {
        await removePushSubscription(sub.endpoint).catch(() => undefined);
        await sub.unsubscribe().catch(() => false);
      }
      return;
    }
    setConfirmedEndpoint(sub.endpoint);
    const prefs = await getPushPreferences().catch(() => null);
    if (!current()) return;
    setCachedPushPreferences(prefs);
    setActive(true);
    lastSyncFailed = false;
  } catch {
    // Offline, 503 before the migration, or a browser that wants a gesture
    // to re-subscribe. State left as is; retried on the next online /
    // foreground, and the Settings card still offers "Turn on".
    lastSyncFailed = true;
  }
};

/** Re-run a sync that failed transiently (called on `online` / foreground). */
export const retryPushSyncIfFailed = (): void => {
  if (lastSyncFailed) void syncPushSubscription();
};

/**
 * Manual logout: stop pushing to this device BEFORE the session is cleared
 * (the call needs the token). The next person to sign in here has to switch
 * it on themselves. Idle logout deliberately does not call this - alerts are
 * the reason to keep a signed-out laptop reachable.
 */
export const releasePushForLogout = async (): Promise<void> => {
  pushEpoch++;
  const wasOptedIn = optInOwner() !== null;
  setOptIn(null);
  setActive(false);
  setCachedPushPreferences(null);
  if (!wasOptedIn) return;
  const sub = await currentSubscription();
  if (!sub) return;
  const release = removePushSubscription(sub.endpoint).catch(() => undefined);
  // Never hold the logout button hostage to a slow network.
  await Promise.race([release, new Promise((resolve) => setTimeout(resolve, 3_000))]);
  // Drop the browser subscription too: even if the server call above didn't
  // land, nothing can reach this device any more (its row then dies at the
  // next 404/410). "Turn on" re-subscribes without a prompt next time.
  await sub.unsubscribe().catch(() => false);
};
